import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useOutletContext } from "react-router-dom";
import CourseYearList from "./CourseList";
import CreateCoursePopup from "../../common/createPopup";
import PagesComponent from "../../common/PagesComponent";
import MarkdownViewer from "../../common/MarkdownViewer";
import MCQViewer from "../../common/MCQViewer";
import TrueFalseViewer from "../../common/TrueFalseViewer";
import MatchViewer from "../../common/MatchViewer";
import FillUpsViewer from "../../common/FillUpsViewer";
import AnalysisComponent from "../../common/AnalysisComponent";
import ContentGeneratorPopup from "../../common/ContentGeneratorPopup";
import MarkdownEditorPopup from "../../common/MarkdownEditorPopup";
import ContentToolbarWrapperComponent from "../../common/ContentToolbarWrapper";
import MaterialViewer from "../../common/MaterialViewer";
import UploadMaterialPopup from "../../common/UploadMaterialPopup";
import ViewMaterialPopup from "../../common/ViewMaterialPopup";
import {
  getPagesForCourse,
  getCourses,
  uploadPageContentFile,
  getPageForCourse,
  removePageContentFile,
} from "../../../helper/apicalls/course";
import {
  getCoursePageEvalStatus,
  evalWithAiMark,
} from "../../../helper/apicalls/eval";

import "./../../../styles/admin/courses.scss";
import GlobalLoader from "../../common/GlobalLoader";
import EmptyPage from "../../common/emptyPage";
import Cookies from "js-cookie";
import SuccessPopup from "../../common/SuccessPopup";
import checkImg from "../../../assets/image/checkImage.png";

const CourseDetails = () => {
  const { key } = useParams();
  const role = Cookies.get("role");
  const permissions = Cookies.get("permissions");
  const permissionsData = permissions ? JSON.parse(permissions) : [];
  const { setHeaderName } = useOutletContext();
  const [openCreateSubjectPopup, setCreateSubjectPopup] = useState(false);
  const [isTestEnabled, setIsTestEnabled] = useState(false);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedPage, setSelectedPage] = useState(null);
  const [cacheSubjectData, setCacheSubjectData] = useState([]);
  const [isGeneratorOpen, setIsGeneratorOpen] = useState(false);
  const [isReplaceMode, setIsReplaceMode] = useState(false);
  const [addAfterIndex, setAddAfterIndex] = useState(null);
  const [isEditorOpen, setIsEditorOpen] = useState(false);
  const [removeCache, setRemoveCache] = useState(false);
  const [isAudioGenerated, setIsAudioGenerated] = useState(false);
  const [isUploadMaterialOpen, setIsUploadMaterialOpen] = useState(false);
  const [selectedChildMaterial, setSelectedChildMaterial] = useState(null);
  const [isMaterialPopupOpen, setIsMaterialPopupOpen] = useState(false);
  const [selectedMaterialForPopup, setSelectedMaterialForPopup] =
    useState(null);

  const [evalStatusByPageId, setEvalStatusByPageId] = useState({});

  const getEvalStatusForPage = (page) => {
    if (!page) return null;
    const id = page?.id;
    const pageKey = page?.key;
    const level = page?.level;
    const virtualKey = level && id ? `${id}-${level}` : null;
    return (
      (virtualKey && evalStatusByPageId[virtualKey]) ||
      (id && evalStatusByPageId[id]) ||
      (pageKey && evalStatusByPageId[pageKey]) ||
      null
    );
  };

  const isPageEvaluated = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return true;
    const status = getEvalStatusForPage(page);
    if (status && typeof status.evaluated !== "undefined")
      return !!status.evaluated;
    if (page.is_eval_content) return true;
    if (page.evalution_score) return true;
    if (
      page.overall_score !== null &&
      typeof page.overall_score !== "undefined"
    )
      return true;
    return false;
  };

  const hasContentToEvaluate = (page) => {
    if (!page) return false;
    const levelStr = (page.level || "").toLowerCase();
    const contentToCheck =
      levelStr === "medium" ? page.medium_content
      : levelStr === "hard" ? page.hard_content
      : page.content;
    
    if (!contentToCheck) return false;
    if (Array.isArray(contentToCheck)) return contentToCheck.length > 0;
    if (typeof contentToCheck === "string") {
      try {
        const parsed = JSON.parse(contentToCheck);
        return Array.isArray(parsed) ? parsed.length > 0 : !!parsed;
      } catch (e) {
        return contentToCheck.trim().length > 0;
      }
    }
    return typeof contentToCheck === "object";
  };

  const isContentPage = (page) => {
    if (!page || !page.id) return false;
    if (page.id === "default-page") return false;
    // Test level sub-pages (easy/medium/hard) are evaluable — level field is merged in
    // from the virtual page on selection; is_virtual may not be present in API response
    if (page.page_type === "test" && page.level) return true;
    if (page.is_virtual) return false;
    
    const type = (page.page_type || "").toLowerCase();
    const parentType = (page.parent_page_type || "").toLowerCase();
    const isSubPageOfQuiz = parentType === "quiz";

    if (type === "content" || type === "quiz" || type === "test" || isSubPageOfQuiz) {
      return true;
    }
    return false;
  };

  const fetchEvalStatus = async () => {
    if (role !== "admin") return;
    try {
      const status = await getCoursePageEvalStatus(key);
      setEvalStatusByPageId(status?.byPageId || {});
    } catch (err) {
      console.error("Error fetching eval status for course pages:", err);
      setEvalStatusByPageId({});
    }
  };

  const handleEvalWithAi = async () => {
    if (!selectedPage || !selectedPage.id) return;
    if (selectedPage.id === "default-page") return;
    if (!isContentPage(selectedPage)) return;
    try {
      setLoading(true);
      const level = selectedPage?.level || null;
      await evalWithAiMark({
        title: "course",
        key,
        pageId: selectedPage.id,
        level,
      });
      await fetchEvalStatus();
    } catch (err) {
      console.error("Error running eval with AI:", err);
    } finally {
      setLoading(false);
    }
  };


  const fetchCourseName = async () => {
    try {
      const courses = await getCourses();

      if (Array.isArray(courses)) {
        const course = courses.find((c) => c.key === key);
        if (course) {
          setHeaderName(`Course Details - ${course.name}`);
        }
      }
    } catch (err) {
      console.error("Error fetching course name:", err);
    }
  };

  const fetchPages = async () => {
    try {
      setLoading(true);
      const data = await getPagesForCourse(key);

      if (Array.isArray(data)) {
        setPages(data);
      } else if (
        data &&
        typeof data === "object" &&
        data.hasOwnProperty("id")
      ) {
        setPages([data]);
      } else {
        setPages([]);
      }
    } catch (err) {
      console.error("Error fetching pages:", err);
      setPages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDialog = () => {
    setCreateSubjectPopup(false);
  };

  const handleToggleTest = () => {
    setIsTestEnabled(!isTestEnabled);
  };

  const handleAddPage = () => {
    fetchPages();
  };

  const handlePageSelect = (page) => {
    setSelectedPage(page);
  };

  const handleUpdatePageContent = (updatedPageData) => {
    setSelectedPage(updatedPageData);
    setRemoveCache(true);
  };

  const handleGeneratorSave = async (updatedPageData) => {
    handleUpdatePageContent(updatedPageData);
    try {
      await fetchPages();
      await fetchEvalStatus();
      setTimeout(() => fetchEvalStatus(), 3000);
      setTimeout(() => fetchEvalStatus(), 6000);
      setTimeout(() => fetchEvalStatus(), 10000);
    } catch (err) {
      console.error("Error refreshing after generator save:", err);
    }
  };

  const handleRemoveMaterial = async (materialId) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);

      await removePageContentFile({
        pageid: selectedPage.id,
        material_id: materialId,
        course_key: key,
      });

      const updatedPageData = await getPageForCourse(
        key,
        selectedPage.id,
        selectedPage.level,
        selectedPage.is_admin_page,
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);
    } catch (error) {
      console.error("Error removing material:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleUploadMaterial = async (materialData) => {
    if (!selectedPage || !selectedPage.id) return;

    try {
      setLoading(true);

      const uploadResponse = await uploadPageContentFile({
        title: "course",
        key: materialData.key,
        pageid: materialData.pageid,
        type: materialData.type,
        file: materialData.file,
        name: materialData.name,
        url: materialData.url,
      });

      const updatedPageData = await getPageForCourse(
        key,
        selectedPage.id,
        selectedPage.level,
        selectedPage.is_admin_page,
      );

      setSelectedPage(updatedPageData);
      setRemoveCache(true);

      await fetchPages();
    } catch (error) {
      console.error("Error uploading material:", error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setSelectedPage(null);
    fetchCourseName();
    fetchPages();
    fetchEvalStatus();
  }, [key, setHeaderName]);

  useEffect(() => {
    setSelectedChildMaterial(null);
  }, [selectedPage]);

  const hasWritePermission = role === "admin" ||
    ((role === "institution" || role === "teacher") && !selectedPage?.is_admin_page);

  return (
    <>
      {loading && <GlobalLoader visible={loading} text="Loading..." />}
      <div className="courses-container">
        <div className="courses-content">
          <div className="course-details-content-right">
            {(() => {
              if (role !== "admin") return null;
              if (!selectedPage || !selectedPage.id) return null;
              if (selectedPage.id === "default-page") return null;
              if (!isContentPage(selectedPage)) return null;

              const evaluated = isPageEvaluated(selectedPage);
              if (evaluated) return null;

              const levelStr = (selectedPage.level || "").toLowerCase();
              const contentToCheck =
                levelStr === "medium" ? selectedPage.medium_content
                : levelStr === "hard" ? selectedPage.hard_content
                : selectedPage.content;
              if (!Array.isArray(contentToCheck) || contentToCheck.length === 0) return null;

              return (
                <div className="eval-actions-bar">
                  <button
                    type="button"
                    className="eval-btn"
                    onClick={handleEvalWithAi}
                  >
                    Evaluate with AI
                  </button>
                </div>
              );
            })()}
            {selectedPage &&
            (selectedPage.content ||
              selectedPage.page_type === "material_item" ||
              selectedPage.page_type === "materials") ? (
              (() => {
                if (selectedPage.page_type === "material_item") {
                  return (
                    <ContentToolbarWrapperComponent
                      onEdit={undefined}
                      onAdd={undefined}
                      onUploadMaterials={() => setIsUploadMaterialOpen(true)}
                      pageType={selectedPage?.page_type}
                      hasContent={Boolean(selectedPage?.url || selectedPage?.file || selectedPage?.material_url || selectedPage?.name)}
                      permission={hasWritePermission}
                      materialType={selectedPage?.material_type || ""}
                      className={(() => {
                        const _lvl = (selectedPage?.level || "").toLowerCase();
                        const _chk = _lvl === "medium" ? selectedPage?.medium_content : _lvl === "hard" ? selectedPage?.hard_content : selectedPage?.content;
                        return role === "admin" && !!selectedPage?.id && selectedPage?.id !== "default-page" && isContentPage(selectedPage) && !isPageEvaluated(selectedPage) && Array.isArray(_chk) && _chk.length > 0 ? "has-eval-actions" : "";
                      })()}
                    >
                      <div className="content-wrapper">
                        <MaterialViewer
                          material={selectedPage}
                          pageData={selectedPage}
                          courseKey={key}
                          subjectKey={null}
                          title="course"
                        />
                      </div>
                    </ContentToolbarWrapperComponent>
                  );
                }

                let contentArray = [];
                try {
                  let contentField = selectedPage?.content;
                  const level = (selectedPage?.level || "").toLowerCase();
                  if (level === "medium" && selectedPage?.medium_content) {
                    contentField = selectedPage?.medium_content;
                  } else if (level === "hard" && selectedPage?.hard_content) {
                    contentField = selectedPage?.hard_content;
                  }

                  if (contentField) {
                    if (typeof contentField === "string") {
                      const trimmed = contentField.trim();
                      if (trimmed) {
                        const parsedContent = JSON.parse(trimmed);
                        if (Array.isArray(parsedContent)) {
                          contentArray = parsedContent;
                        } else if (parsedContent) {
                          contentArray = [parsedContent];
                        }
                      }
                    } else if (Array.isArray(contentField)) {
                      contentArray = contentField;
                    } else if (typeof contentField === "object") {
                      contentArray = [contentField];
                    }
                  }
                } catch (e) {
                  contentArray = [{ content: selectedPage?.content }];
                }
                const hasContent = contentArray && contentArray.length > 0;

                return (
                  <ContentToolbarWrapperComponent
                    onEdit={() => setIsEditorOpen(true)}
                    onAdd={() => {
                      setIsReplaceMode(false);
                      setIsGeneratorOpen(true);
                    }}
                    onReplace={() => {
                      setIsReplaceMode(true);
                      setIsGeneratorOpen(true);
                    }}
                    onUploadMaterials={() => setIsUploadMaterialOpen(true)}
                    pageType={selectedPage?.page_type}
                    permission={hasWritePermission}
                    hasContent={hasContent}
                    disableAdd={selectedPage?.page_type === "quiz"}
                    audioBook={selectedPage?.tts_s3_url || null}
                    layer={"course"}
                    id={selectedPage?.id}
                    setIsAudioGenerated={setIsAudioGenerated}
                    materialType={selectedPage?.material_type || ""}
                    className={(() => {
                      const _lvl = (selectedPage?.level || "").toLowerCase();
                      const _chk = _lvl === "medium" ? selectedPage?.medium_content : _lvl === "hard" ? selectedPage?.hard_content : selectedPage?.content;
                      return role === "admin" && !!selectedPage?.id && selectedPage?.id !== "default-page" && isContentPage(selectedPage) && !isPageEvaluated(selectedPage) && Array.isArray(_chk) && _chk.length > 0 ? "has-eval-actions" : "";
                    })()}
                  >
                    <div className="content-wrapper">
                      {contentArray?.length > 0 ? (
                        contentArray.map((item, index) => {
                          let contentType = "content";
                          let contentToDisplay = item;

                          let parsedItem = item;
                          if (typeof item === "string") {
                            try {
                              parsedItem = JSON.parse(item);
                            } catch (e) {
                              parsedItem = item;
                            }
                          }

                          if (
                            parsedItem &&
                            typeof parsedItem === "object" &&
                            parsedItem.type
                          ) {
                            contentType = parsedItem.type;
                            contentToDisplay = parsedItem.content || parsedItem;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.type
                          ) {
                            contentType = item.type;
                            contentToDisplay = item.content;
                          } else if (
                            item &&
                            typeof item === "object" &&
                            item.content
                          ) {
                            contentToDisplay = item.content;
                          }
                          switch (contentType) {
                            case "material_item":
                            case "pdf":
                            case "video":
                            case "audio":
                            case "image":
                            case "ppt":
                            case "doc":
                              return (
                                <div key={`material-${index}`}>
                                  <MaterialViewer
                                    material={item}
                                    pageData={selectedPage}
                                    courseKey={key}
                                    subjectKey={null}
                                    onRemove={handleRemoveMaterial}
                                    title="course"
                                  />
                                </div>
                              );
                            case "mcq":
                              return (
                                <MCQViewer
                                  key={`mcq-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "trueorfalse":
                              return (
                                <TrueFalseViewer
                                  key={`tf-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "match":
                              return (
                                <MatchViewer
                                  key={`match-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "fillups":
                              return (
                                <FillUpsViewer
                                  key={`fillups-${index}`}
                                  questionData={contentToDisplay}
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            case "analysis":
                              return (
                                <AnalysisComponent
                                  key={`analysis-${index}`}
                                  data={contentToDisplay}
                                  title="Conversational Analysis"
                                  onUpdate={handleUpdatePageContent}
                                  pageId={selectedPage?.id}
                                  pageData={selectedPage}
                                  context="course"
                                  courseKey={key}
                                  contentIndex={index}
                                  onAddNext={() => {
                                    setAddAfterIndex(index);
                                    setIsGeneratorOpen(true);
                                  }}
                                  permission={hasWritePermission}
                                />
                              );
                            default:
                              return (
                                <MarkdownViewer
                                  key={index}
                                  content={
                                    typeof contentToDisplay === "string"
                                      ? contentToDisplay
                                      : typeof contentToDisplay?.markdown ===
                                          "string"
                                        ? contentToDisplay.markdown
                                        : typeof contentToDisplay?.markdown
                                              ?.content === "string"
                                          ? contentToDisplay.markdown.content
                                          : JSON.stringify(contentToDisplay)
                                  }
                                  pageId={selectedPage?.id}
                                  context="course"
                                  courseKey={key}
                                  onUpdate={handleUpdatePageContent}
                                  permission={role === "admin"}
                                />
                              );
                          }
                        })
                      ) : (
                        <>
                          <EmptyPage content="No content Available" />
                        </>
                      )}
                    </div>
                  </ContentToolbarWrapperComponent>
                );
              })()
            ) : (
              <CourseYearList
                hideBreadcrumbs={true}
                setCacheSubjectData={setCacheSubjectData}
                cacheSubjectData={cacheSubjectData}
                showActionBtn={role === "admin"}
                role={role}
              />
            )}
          </div>
          <div className="pages-component-container">
            <PagesComponent
              pages={pages}
              onAddPage={handleAddPage}
              onToggleTest={handleToggleTest}
              isTestEnabled={isTestEnabled}
              addButtonText="Add Page"
              courseKey={key}
              onPageSelect={handlePageSelect}
              selectedPageData={selectedPage}
              removeCache={removeCache}
              setRemoveCache={setRemoveCache}
              evalStatusByPageId={evalStatusByPageId}
            />
          </div>
        </div>
        {openCreateSubjectPopup && (
          <CreateCoursePopup
            open={openCreateSubjectPopup}
            handleClose={handleCloseDialog}
            title="Subject"
          />
        )}

        {isGeneratorOpen && (
          <ContentGeneratorPopup
            open={isGeneratorOpen}
            handleClose={() => {
              setIsGeneratorOpen(false);
              setAddAfterIndex(null);
              setIsReplaceMode(false);
            }}
            onSave={handleGeneratorSave}
            isReplaceMode={isReplaceMode}
            existingContent={
              (selectedPage?.level === "medium"
                ? selectedPage?.medium_content
                : selectedPage?.level === "hard"
                ? selectedPage?.hard_content
                : selectedPage?.content) || ""
            }
            pageId={selectedPage?.id || null}
            pageData={selectedPage || null}
            context="course"
            courseKey={key}
            insertionIndex={
              typeof addAfterIndex === "number"
                ? addAfterIndex
                : Array.isArray(selectedPage?.content)
                  ? selectedPage?.content.length - 1
                  : -1
            }
          />
        )}
        {isAudioGenerated && (
          <SuccessPopup
            open={isAudioGenerated}
            onClose={() => setIsAudioGenerated(false)}
            iconSrc={checkImg}
          />
        )}

        {isEditorOpen && (
          <MarkdownEditorPopup
            open={isEditorOpen}
            handleClose={() => setIsEditorOpen(false)}
            title="Edit Content"
            initialValue={selectedPage?.content || ""}
            onSave={(updatedPageData) => {
              handleUpdatePageContent(updatedPageData);
              setIsEditorOpen(false);
            }}
            context="course"
            pageId={selectedPage?.id || null}
            pageData={selectedPage || null}
            courseKey={key}
          />
        )}

        {isUploadMaterialOpen && (
          <UploadMaterialPopup
            open={isUploadMaterialOpen}
            handleClose={() => setIsUploadMaterialOpen(false)}
            onSave={handleUploadMaterial}
            materialType={selectedPage?.material_type || ""}
            pageData={
              selectedPage?.page_type === "materials"
                ? selectedChildMaterial || selectedPage
                : selectedPage
            }
            context="course"
            courseKey={key}
            subjectKey={null}
            pageType={selectedPage?.page_type}
          />
        )}

        {isMaterialPopupOpen && (
          <ViewMaterialPopup
            open={isMaterialPopupOpen}
            handleClose={() => setIsMaterialPopupOpen(false)}
            material={selectedMaterialForPopup}
          />
        )}
      </div>
    </>
  );
};

export default CourseDetails;
